import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchStocks, stockPath } from "../lib/stockApi";

// Type-ahead symbol search: Enter or a click opens /symbols/EXCHANGE:SYMBOL.
export default function StockSearch({ className = "", autoFocus = false, onDone }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      searchStocks(term)
        .then((d) => live && (setResults(d.results || []), setActive(0), setOpen(true)))
        .catch(() => live && setResults([]));
    }, 180);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);

  useEffect(() => {
    const close = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (r) => {
    const target = r ? stockPath(r.exsym) : `/symbols/${encodeURIComponent(q.trim()).replace(/%3A/gi, ":")}`;
    if (!r && !q.trim()) return;
    setQ("");
    setOpen(false);
    onDone?.();
    navigate(target);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ab-faint)] pointer-events-none" />
      <input
        type="search"
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search symbols, e.g. GP or Apple"
        aria-label="Search symbols"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--ab-border)] bg-[var(--ab-surface)] text-sm text-[var(--ab-text)] placeholder:text-[var(--ab-faint)] focus:outline-none focus:border-[var(--ab-accent)]"
      />
      {open && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-80 overflow-auto rounded-lg border border-[var(--ab-border)] bg-[var(--ab-surface)] py-1"
          style={{ boxShadow: "var(--ab-shadow)" }}
        >
          {results.map((r, i) => (
            <li key={r.exsym} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(r)}
                className={`w-full text-left px-3 py-2 flex items-baseline gap-2 ${i === active ? "bg-[var(--ab-accent-soft)]" : ""}`}
              >
                <span className="font-mono text-xs font-semibold text-[var(--ab-text)] shrink-0">{r.exsym}</span>
                <span className="text-sm text-[var(--ab-muted)] truncate">{r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
